import { useMemo, useState } from 'react';
import { useCurrencyFormat } from '@/hooks/use-currency-format';
import { format, addDays } from 'date-fns';
import { ChevronDown, ChevronUp, Check, X, Info, RefreshCw, Link2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { Competitor, SuggestionStatus, DEFAULT_PRICING_STRATEGY, PricingAnalysisRow } from '@/types/competitor';
import {
  CompetitorHeatmapRow,
  CompetitorHeatmapCell,
  getHeatLevel,
  HEAT_LEVEL_COLORS,
} from '@/types/room-variant-mapping';
import { RoomMapping, getCompetitorRooms } from './AnalysisRoomMappingDialog';
import competitorRawData from '@/data/competitor_raw_data.json';

interface CompetitorHeatmapGridProps {
  competitors: Competitor[];
  basePrice: number;
  startDate: Date;
  roomVariant?: string;
  otaChannel?: string;
  onAccept: (date: string) => void;
  onReject: (date: string) => void;
  onAcceptAll: () => void;
  onRejectAll: () => void;
  suggestionStatuses: Record<string, SuggestionStatus>;
  selectedDates: string[];
  onToggleDateSelection: (date: string) => void;
  onSelectAllDates: (dates: string[]) => void;
  isLoading?: boolean;
  onRefresh?: () => void;
  roomMappings?: RoomMapping[];
  onOpenMapping?: () => void;
  pricingAnalysisData?: PricingAnalysisRow[]; // Add pricing analysis data from API
}

// Smart pricing algorithm
function calculateSuggestedPrice(
  yourPrice: number,
  competitorPrices: number[],
  occupancy: number
): { price: number; strategy: 'maintain' | 'increase' | 'decrease' } {
  if (competitorPrices.length === 0) {
    return { price: yourPrice, strategy: 'maintain' };
  }

  const competitorAvg = competitorPrices.reduce((a, b) => a + b, 0) / competitorPrices.length;
  const priceDiffPercent = ((yourPrice - competitorAvg) / competitorAvg) * 100;
  const { low: lowOccupancy, high: highOccupancy } = DEFAULT_PRICING_STRATEGY.occupancyThresholds;

  let suggestedPrice = yourPrice;
  let strategy: 'maintain' | 'increase' | 'decrease' = 'maintain';

  if (priceDiffPercent > 10 && occupancy < lowOccupancy) {
    suggestedPrice = Math.round(yourPrice * 0.9);
    strategy = 'decrease';
  } else if (priceDiffPercent < -10 && occupancy > highOccupancy) {
    suggestedPrice = Math.round(yourPrice * 1.15);
    strategy = 'increase';
  } else if (occupancy > highOccupancy) {
    suggestedPrice = Math.round(yourPrice * 1.05);
    strategy = 'increase';
  } else if (occupancy < lowOccupancy) {
    suggestedPrice = Math.round(yourPrice * 0.95);
    strategy = 'decrease';
  }

  return { price: suggestedPrice, strategy };
}

// Get mapped competitor price from raw data
function getMappedCompetitorPrice(
  mapping: RoomMapping,
  dateStr: string
): number | null {
  const competitorData = (competitorRawData as any[]).find(
    (item) =>
      item.property_name === mapping.competitorProperty &&
      item.room_type_name === mapping.competitorRoomName &&
      item.platform === mapping.competitorPlatform &&
      item.checkin === dateStr
  );
  
  if (competitorData && competitorData.price_per_night) {
    return parseInt(competitorData.price_per_night, 10);
  }
  return null;
}

export function CompetitorHeatmapGrid({
  competitors,
  basePrice,
  startDate,
  roomVariant,
  otaChannel,
  onAccept,
  onReject,
  onAcceptAll,
  onRejectAll,
  suggestionStatuses,
  selectedDates,
  onToggleDateSelection,
  onSelectAllDates,
  isLoading = false,
  onRefresh,
  roomMappings = [],
  onOpenMapping,
  pricingAnalysisData = [],
}: CompetitorHeatmapGridProps) {
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const { format: formatPrice } = useCurrencyFormat();

  const activeCompetitors = competitors.filter(c => c.is_active);
  
  console.log('CompetitorHeatmapGrid - Active Competitors:', activeCompetitors);
  console.log('CompetitorHeatmapGrid - Pricing Analysis Data:', pricingAnalysisData);
  
  // Get unique mapped competitor properties
  const mappedCompetitorProperties = useMemo(() => {
    const uniqueProps = new Map<string, { property: string; platform: string; roomName: string }>();
    roomMappings.forEach((m) => {
      const key = `${m.competitorProperty}-${m.competitorPlatform}`;
      if (!uniqueProps.has(key)) {
        uniqueProps.set(key, {
          property: m.competitorProperty,
          platform: m.competitorPlatform,
          roomName: m.competitorRoomName,
        });
      }
    });
    return Array.from(uniqueProps.values());
  }, [roomMappings]);

  // Generate heatmap data from pricing analysis data
  const heatmapData: CompetitorHeatmapRow[] = useMemo(() => {
    const data: CompetitorHeatmapRow[] = [];
    
    // If no pricing analysis data, return empty array
    if (!pricingAnalysisData || pricingAnalysisData.length === 0) {
      return [];
    }

    // Process each day from the pricing analysis data
    pricingAnalysisData.slice(0, 15).forEach((analysisRow) => {
      const dateStr = analysisRow.date;
      const dayOfWeek = analysisRow.dayOfWeek;
      const yourPrice = analysisRow.yourPrice || basePrice;
      const marketAvg = analysisRow.marketAvg;
      const occupancy = analysisRow.occupancy;
      const suggestedPrice = analysisRow.suggestedPrice;
      
      // Create competitor prices object from API data
      const competitorPrices: Record<string, CompetitorHeatmapCell> = {};
      const allPrices: number[] = [];
      
      // Process competitors from API data if available
      if (analysisRow.competitors && analysisRow.competitors.length > 0) {
        const processedCompetitors = new Set<string>();
        
        analysisRow.competitors.forEach(comp => {
          const compId = `api-${comp.property_name}-${comp.platform}`;
          const price = typeof comp.price_per_night === 'number' ? 
            comp.price_per_night : 
            parseInt(comp.price_per_night, 10);
          
          // Skip if already processed this competitor
          if (processedCompetitors.has(compId)) {
            return;
          }
          processedCompetitors.add(compId);
          
          const priceVsYours = price - yourPrice;
          const priceVsYoursPercent = (priceVsYours / yourPrice) * 100;
          allPrices.push(price);
          
          competitorPrices[compId] = {
            competitorId: compId,
            competitorName: comp.property_name,
            date: dateStr,
            price,
            priceVsYours,
            priceVsYoursPercent,
            heatLevel: getHeatLevel(priceVsYoursPercent),
            otaChannel: comp.platform,
            roomVariant: comp.room_type_name,
          };
        });
      }
      
      // Match active competitors with API data and update keys to use comp.id
      activeCompetitors.forEach((comp) => {
        // Find ALL matching entries for this competitor (could be multiple platforms)
        const matchingEntries = Object.entries(competitorPrices).filter(
          ([key, c]) => {
            const apiName = c.competitorName.toLowerCase();
            const localName = comp.name.toLowerCase();
            // Check if either name contains the other, or they're equal
            return apiName === localName || 
                   apiName.includes(localName) || 
                   localName.includes(apiName) ||
                   // Also check if the first significant words match (e.g., "MAYFAIR Hill")
                   apiName.split(' ').slice(0, 2).join(' ') === localName.split(' ').slice(0, 2).join(' ');
          }
        );
        
        if (matchingEntries.length > 0) {
          // Use the first matching entry for the main cell display
          const [firstKey, firstEntry] = matchingEntries[0];
          
          // Calculate average price across all matching entries
          const totalPrice = matchingEntries.reduce((sum, [_, entry]) => sum + entry.price, 0);
          const avgPrice = Math.round(totalPrice / matchingEntries.length);
          
          // Collect all unique room types from matching entries
          const roomTypes = matchingEntries
            .map(([_, entry]) => entry.roomVariant)
            .filter((rt, idx, arr) => rt && rt !== 'N/A' && arr.indexOf(rt) === idx);
          const roomVariant = roomTypes.length > 0 ? roomTypes.join(', ') : firstEntry.roomVariant || 'N/A';
          
          // Update the main entry with the competitor's ID as the key and average price
          competitorPrices[comp.id] = {
            ...firstEntry,
            competitorId: comp.id,
            price: avgPrice,
            priceVsYours: avgPrice - yourPrice,
            priceVsYoursPercent: ((avgPrice - yourPrice) / yourPrice) * 100,
            heatLevel: getHeatLevel(((avgPrice - yourPrice) / yourPrice) * 100),
            roomVariant, // Use collected room types
          };
          
          // Remove all old API entries to avoid duplicates
          matchingEntries.forEach(([key]) => {
            if (key !== comp.id) {
              delete competitorPrices[key];
            }
          });
        } else {
          // If competitor is not in API data, add a single placeholder entry with price 0
          // Use comp.id as the key directly so it matches the lookup in the render
          competitorPrices[comp.id] = {
            competitorId: comp.id,
            competitorName: comp.name,
            date: dateStr,
            price: 0, // No price data available
            priceVsYours: 0,
            priceVsYoursPercent: 0,
            heatLevel: 'similar', // No data, show as neutral
            otaChannel: comp.trackedOTAs[0] || 'booking',
            roomVariant: 'N/A',
          };
        }
      });
      
      // Add mapped competitor prices from roomMappings
      roomMappings.forEach((mapping) => {
        const price = getMappedCompetitorPrice(mapping, dateStr);
        if (price !== null) {
          const key = `${mapping.competitorProperty}-${mapping.competitorPlatform}`;
          const priceVsYours = price - yourPrice;
          const priceVsYoursPercent = (priceVsYours / yourPrice) * 100;
          
          competitorPrices[`mapped-${key}`] = {
            competitorId: `mapped-${key}`,
            competitorName: mapping.competitorProperty,
            date: dateStr,
            price: price,
            priceVsYours,
            priceVsYoursPercent,
            heatLevel: getHeatLevel(priceVsYoursPercent),
            otaChannel: mapping.competitorPlatform,
            roomVariant: mapping.competitorRoomName,
            isMapped: true,
          } as CompetitorHeatmapCell & { isMapped?: boolean };
          
          allPrices.push(price);
        }
      });

      data.push({
        date: dateStr,
        dayOfWeek,
        yourPrice,
        marketAvg,
        occupancy,
        competitorPrices,
        suggestedPrice,
        suggestionStatus: suggestionStatuses[dateStr] || 'pending',
      });
    });

    return data;
  }, [pricingAnalysisData, basePrice, activeCompetitors, suggestionStatuses, roomMappings]);

  const allDates = heatmapData.map(r => r.date);

  if (activeCompetitors.length === 0) {
    return (
      <Card className="border-amber-500/30 bg-amber-500/5">
        <CardContent className="py-8 text-center">
          <p className="text-sm font-medium text-amber-600">No Active Competitors</p>
          <p className="text-sm text-muted-foreground mt-1">
            Add and activate competitors in the "Manage Competitors" tab to see the heatmap.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Legend */}
      <Card>
        <CardContent className="py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium">Price vs Yours:</span>
            <div className="flex items-center gap-2">
              {Object.entries(HEAT_LEVEL_COLORS).map(([level, colors]) => (
                <div key={level} className="flex items-center gap-1">
                  <div className={cn('w-4 h-4 rounded', colors.bg, colors.border, 'border')} />
                  <span className="text-xs text-muted-foreground capitalize">
                    {level.replace('-', ' ')}
                  </span>
                </div>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {onRefresh && (
              <Button
                size="sm"
                variant="outline"
                className="gap-2"
                onClick={onRefresh}
                disabled={isLoading}
              >
                <RefreshCw className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`} />
                {isLoading ? "Loading..." : "Refresh"}
              </Button>
            )}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <Info className="h-4 w-4 text-muted-foreground" />
                </TooltipTrigger>
                <TooltipContent>
                  <p>Green = competitor is more expensive (opportunity)</p>
                  <p>Red = competitor is cheaper (risk)</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </CardContent>
      </Card>

      {/* Heatmap Grid */}
      <Card>
        <ScrollArea className="w-full">
          <div className="min-w-[800px]">
            {/* Header */}
            <div className="flex border-b bg-muted/50">
              <div className="w-12 p-2 flex-shrink-0 flex items-center justify-center">
                <Checkbox
                  checked={selectedDates.length === allDates.length}
                  onCheckedChange={(checked) => {
                    onSelectAllDates(checked ? allDates : []);
                  }}
                />
              </div>
              <div className="w-28 p-2 text-xs font-medium text-muted-foreground flex-shrink-0">Date</div>
              <div className="w-24 p-2 text-xs font-medium text-muted-foreground text-right flex-shrink-0">Your Price</div>
              
              {/* Competitor columns - use flex-1 to fill space evenly */}
              {activeCompetitors.map((comp) => (
                <div
                  key={comp.id}
                  className="flex-1 min-w-[120px] p-2 text-xs font-medium text-center truncate border-x border-border/50"
                  title={comp.name}
                >
                  <div className="truncate font-semibold">{comp.name}</div>
                  <Badge variant="outline" className="text-[10px] mt-0.5">
                    {comp.category}
                  </Badge>
                </div>
              ))}
              
              <div className="w-24 p-2 text-xs font-medium text-muted-foreground text-right flex-shrink-0">Mkt Avg</div>
              <div className="w-24 p-2 text-xs font-medium text-muted-foreground text-right flex-shrink-0">Suggested</div>
              <div className="w-16 p-2 text-xs font-medium text-muted-foreground text-center flex-shrink-0">Occ%</div>
              <div className="w-28 p-2 text-xs font-medium text-muted-foreground text-center flex-shrink-0">Action</div>
              <div className="w-10 flex-shrink-0" />
            </div>

            {/* Data rows */}
            {heatmapData.map((row) => (
              <div key={row.date}>
                <div
                  className={cn(
                    'flex border-b hover:bg-muted/30 cursor-pointer transition-colors',
                    selectedDates.includes(row.date) && 'bg-muted/50',
                    row.suggestionStatus === 'accepted' && 'bg-green-50/50',
                    row.suggestionStatus === 'rejected' && 'bg-red-50/30'
                  )}
                  onClick={() => setExpandedRow(expandedRow === row.date ? null : row.date)}
                >
                  <div className="w-12 p-2 flex-shrink-0 flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={selectedDates.includes(row.date)}
                      onCheckedChange={() => onToggleDateSelection(row.date)}
                    />
                  </div>
                  <div className="w-28 p-2 flex-shrink-0">
                    <div className="text-sm font-medium">{format(new Date(row.date), 'MMM dd')}</div>
                    <div className="text-xs text-muted-foreground">{row.dayOfWeek}</div>
                  </div>
                  <div className="w-24 p-2 text-right flex-shrink-0">
                    <span className="font-semibold">{formatPrice(row.yourPrice)}</span>
                  </div>

                  {/* Competitor heatmap cells */}
                  {activeCompetitors.map((comp) => {
                    const cell = row.competitorPrices[comp.id];
                    
                    // Show empty cell if no data at all
                    if (!cell) {
                      return (
                        <div key={comp.id} className="flex-1 min-w-[120px] p-2 bg-muted/30 text-center border-x border-border/50 flex items-center justify-center">
                          <span className="text-xs text-muted-foreground">No data</span>
                        </div>
                      );
                    }
                    
                    // Show "No data" for price 0 (placeholder entries)
                    if (cell.price === 0) {
                      return (
                        <div key={comp.id} className="flex-1 min-w-[120px] p-2 bg-muted/30 text-center border-x border-border/50 flex items-center justify-center">
                          <span className="text-xs text-muted-foreground">No data</span>
                        </div>
                      );
                    }
                    
                    const colors = HEAT_LEVEL_COLORS[cell.heatLevel];
                    
                    return (
                      <TooltipProvider key={comp.id}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div
                              className={cn(
                                'flex-1 min-w-[120px] p-2 text-center',
                                colors.bg,
                                'border-x border-border/50',
                                colors.border
                              )}
                            >
                              <div className={cn('font-semibold text-sm', colors.text)}>
                                {formatPrice(cell.price)}
                              </div>
                              <div className={cn('text-[10px] truncate', colors.text)} title={cell.roomVariant}>
                                {cell.roomVariant && cell.roomVariant !== 'N/A' ? cell.roomVariant : ''}
                              </div>
                              <div className={cn('text-xs font-medium', colors.text)}>
                                {cell.priceVsYoursPercent > 0 ? '+' : ''}{cell.priceVsYoursPercent.toFixed(0)}%
                              </div>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>
                            <div className="text-sm">
                              <p className="font-medium">{cell.competitorName}</p>
                              <p>Room: {cell.roomVariant || 'N/A'}</p>
                              <p>Price: {formatPrice(cell.price)}</p>
                              <p>vs Yours: {cell.price > row.yourPrice ? '+' : '-'}{formatPrice(Math.abs(cell.price - row.yourPrice))}</p>
                              <p>OTA: {cell.otaChannel}</p>
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    );
                  })}

                  {/* Mapped competitor heatmap cells */}
                  {/* {mappedCompetitorProperties.map((mapped) => {
                    const key = `mapped-${mapped.property}-${mapped.platform}`;
                    const cell = row.competitorPrices[key] as (CompetitorHeatmapCell & { isMapped?: boolean }) | undefined;
                    
                    if (!cell) {
                      return (
                        <div 
                          key={key} 
                          className="w-28 p-2 flex-shrink-0 bg-muted/30 text-center"
                        >
                          <span className="text-xs text-muted-foreground">No data</span>
                        </div>
                      );
                    }
                    
                    const colors = HEAT_LEVEL_COLORS[cell.heatLevel];
                    
                    return (
                      <TooltipProvider key={key}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div
                              className={cn(
                                'w-28 p-2 text-center flex-shrink-0',
                                colors.bg,
                                'border-x border-primary/20',
                                colors.border
                              )}
                            >
                              <div className={cn('font-medium text-sm flex items-center justify-center gap-1', colors.text)}>
                                <Link2 className="h-3 w-3" />
                                {formatPrice(cell.price)}
                              </div>
                              <div className={cn('text-[10px] truncate', colors.text)} title={cell.roomVariant}>
                                {cell.roomVariant && cell.roomVariant !== 'N/A' ? cell.roomVariant : ''}
                              </div>
                              <div className={cn('text-xs', colors.text)}>
                                {cell.priceVsYoursPercent > 0 ? '+' : ''}{cell.priceVsYoursPercent.toFixed(0)}%
                              </div>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>
                            <div className="text-sm">
                              <p className="font-medium flex items-center gap-1">
                                <Link2 className="h-3 w-3" />
                                {mapped.property}
                              </p>
                              <p>Room: {mapped.roomName}</p>
                              <p>Price: {formatPrice(cell.price)}</p>
                              <p>vs Yours: {cell.price > row.yourPrice ? '+' : '-'}{formatPrice(Math.abs(cell.price - row.yourPrice))}</p>
                              <p>Platform: {cell.otaChannel}</p>
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    );
                  })} */}

                  <div className="w-24 p-2 text-right flex-shrink-0">
                    <span className={cn(
                      'font-semibold',
                      row.marketAvg > row.yourPrice ? 'text-green-600' : 'text-destructive'
                    )}>
                      {formatPrice(row.marketAvg)}
                    </span>
                  </div>
                  <div className="w-24 p-2 text-right flex-shrink-0">
                    <span className="font-semibold text-primary">{formatPrice(row.suggestedPrice)}</span>
                  </div>
                  <div className="w-16 p-2 text-center flex-shrink-0">
                    <Badge variant={row.occupancy >= 80 ? 'default' : row.occupancy >= 60 ? 'secondary' : 'outline'}>
                      {row.occupancy}%
                    </Badge>
                  </div>
                  <div className="w-28 p-2 flex items-center justify-center gap-2 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                    <Button
                      variant={row.suggestionStatus === 'accepted' ? 'default' : 'ghost'}
                      size="icon"
                      className={cn(
                        'h-7 w-7',
                        row.suggestionStatus === 'accepted' && 'bg-green-600 hover:bg-green-700'
                      )}
                      onClick={() => onAccept(row.date)}
                    >
                      <Check className="h-4 w-4" />
                    </Button>
                    <Button
                      variant={row.suggestionStatus === 'rejected' ? 'destructive' : 'ghost'}
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => onReject(row.date)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="w-10 p-2 flex items-center justify-center flex-shrink-0">
                    {expandedRow === row.date ? (
                      <ChevronUp className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    )}
                  </div>
                </div>

                {/* Expanded row with details */}
                {expandedRow === row.date && (
                  <div className="bg-muted/20 border-b p-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {activeCompetitors.map((comp) => {
                        const cell = row.competitorPrices[comp.id];
                        if (!cell) return null;
                        
                        return (
                          <Card key={comp.id} className="border">
                            <CardContent className="p-3">
                              <div className="flex items-center justify-between mb-2">
                                <span className="font-medium text-sm truncate">{comp.name}</span>
                                <Badge variant="outline" className="px-2 py-1">
                                  {comp.starRating}★
                                </Badge>
                              </div>
                              <div className="space-y-1 text-sm">
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Price:</span>
                                  <span className="font-medium">{formatPrice(cell.price)}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">vs Yours:</span>
                                  <span className={cn(
                                    'font-medium',
                                    cell.price > row.yourPrice ? 'text-green-600' : 'text-destructive'
                                  )}>
                                    {cell.price > row.yourPrice ? '+' : '-'}{formatPrice(Math.abs(cell.price - row.yourPrice))}
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">OTA:</span>
                                  <span>{cell.otaChannel}</span>
                                </div>
                                {/* <div className="flex justify-between">
                                  <span className="text-muted-foreground">Distance:</span>
                                  <span>{comp.distance} {comp.distanceUnit}</span>
                                </div> */}
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
          <ScrollBar orientation="horizontal" />
        </ScrollArea>
      </Card>
    </div>
  );
}
